/** PaintingFragmentStore — 章ごとの「名画ピース」収集数を永続化(LiveOps リテンション起点)。
 *  Reference: docs/CLAUDE.md §3.5 Living World, MONETIZATION_EXTENSIONS.md Card Collection
 *  - chapter ごとに 100 個集めると「章の名画」が解放され、Gallery に飾れる
 *  - Phase A はカウント+toast のみ実装、ギャラリー画面は別ロットで実装予定
 *
 *  Callers:
 *    - web/src/main.ts (初期化 + onPaintingFragmentGained callback)
 *    - web/src/ui/screens/HomeScreen.ts (getPaintingProgress option)
 *    - web/src/ui/screens/GameplayScreen.ts (collectible pickup: 'painting_fragment' case)
 */

export interface PaintingProgress {
  /** chapter id → 収集ピース数(0..FRAGMENTS_PER_PAINTING) */
  readonly [chapterId: string]: number;
}

const STORAGE_KEY = 'ck.paintingFragments';
const FRAGMENTS_PER_PAINTING = 100;

export class PaintingFragmentStore {
  private progress: { [k: string]: number };

  constructor() {
    this.progress = this.load();
  }

  private load(): { [k: string]: number } {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          const result: { [k: string]: number } = {};
          for (const [k, v] of Object.entries(parsed)) {
            if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
              result[k] = Math.floor(v);
            }
          }
          return result;
        }
      }
    } catch {
      /* localStorage が使えない環境はスキップ */
    }
    return {};
  }

  private save(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.progress));
    } catch {
      /* noop */
    }
  }

  /** chapter id(数値→string 化)で現在のピース数を返す */
  count(chapter: number): number {
    return this.progress[String(chapter)] ?? 0;
  }

  /** 加算。FRAGMENTS_PER_PAINTING に初めて到達した時に completed=true */
  add(chapter: number, n = 1): { newCount: number; completed: boolean } {
    const key = String(chapter);
    const before = this.progress[key] ?? 0;
    const next = before + n;
    this.progress[key] = next;
    this.save();
    return {
      newCount: next,
      completed: before < FRAGMENTS_PER_PAINTING && next >= FRAGMENTS_PER_PAINTING,
    };
  }

  /** 章ごとの目標(画面表示用) */
  fragmentsPerPainting(): number {
    return FRAGMENTS_PER_PAINTING;
  }
}
